import { useCallback, useEffect } from 'react'

// Publishes the horizontal scroller's visible width as --lbv2-port-width.
//
// The full-width rows - cut line, section line, totals, the starting-hole note
// - span every column and centre their label, so they have to be as wide as
// what the reader can see rather than as wide as the table. That was expressed
// as 100vw, which includes the scrollbar while the container does not: wherever
// scrollbars take space (always on Windows, and on macOS set to "Always") the
// row came out ~15px too wide. Being inside a cell that spans every column,
// that pushed the table's min-content past its container and produced a
// horizontal scrollbar nothing had asked for - which then justified itself.
//
// clientWidth excludes the scrollbar by definition, so it is the honest number.
// onMeasured re-runs whatever depends on the content width: publishing this
// resizes the full-width rows, and the scrollability check that decides the
// scroll rail was taken before that - against the oversized table.
const usePortWidth = (scrollRef, onMeasured) => {
  const measure = useCallback(() => {
    const el = scrollRef.current
    if (!el) {
      return
    }

    el.style.setProperty('--lbv2-port-width', `${el.clientWidth}px`)

    if (onMeasured) {
      onMeasured()
    }
  }, [ scrollRef, onMeasured ])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) {
      return undefined
    }

    measure()

    const observer = new ResizeObserver(measure)
    observer.observe(el)

    return () => observer.disconnect()
  }, [ scrollRef, measure ])
}

export default usePortWidth
